import { StrictMode, Suspense, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import ReactDOM from 'react-dom/client'
// Used for Calendar
import 'temporal-polyfill/global'
import {
  Outlet,
  type RootRoute,
  RouterProvider,
  createRootRoute,
  createRouter,
  useLocation,
  useRouter,
} from '@tanstack/react-router'

// import { TanStackDevtools } from '@tanstack/react-devtools'
// import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
// import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'

import Header from './components/Header'
import { AppUpdateNotifier } from './components/AppUpdateNotifier'
import { FeedbackProvider } from './components/FeedbackProvider'
import { FollowUpsProvider } from './components/FollowUpsProvider'
import { NewCalloutModalProvider } from './components/NewCalloutModalProvider'
import { NewJobModalProvider } from './components/NewJobModalProvider'
import { NewSessionModalProvider } from './components/NewSessionModalProvider'
import { RootErrorBoundary } from './components/RootErrorBoundary'
import { RootPending } from './components/RootPending'
import { AuthProvider, useAuth } from '@/lib/auth'
import { useDb } from '@/lib/dbContext'
import { DbProvider } from '@/lib/dbContext'
import { createDb } from '@/lib/db'
import { registerDbConsoleRepl } from '@/lib/dev-console-repl'
import { getRuntimeEnv, loadRuntimeEnv } from '@/lib/runtimeEnv'
import { initSentry, Sentry } from '@/lib/sentry'
import {
  createPocketBaseAuth,
  createPocketBaseTanStackDb,
  pocketBaseIdGenerator,
} from '@/db/adapters/pocketbase'

import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'

import './styles.css'
import reportWebVitals from './reportWebVitals.ts'

import registerRoutes from './routes.ts'

function RouteTheme(props: { theme: 'light' | 'dark'; children: ReactNode }) {
  useEffect(() => {
    const html = document.documentElement
    const previousTheme = html.dataset.routeTheme
    html.dataset.routeTheme = props.theme

    return () => {
      if (previousTheme) {
        html.dataset.routeTheme = previousTheme
      } else {
        delete html.dataset.routeTheme
      }
    }
  }, [props.theme])

  return <>{props.children}</>
}

function RootLayout() {
  const router = useRouter()
  const { user, logout, refreshSession } = useAuth()
  const db = useDb()
  const location = useLocation()

  const isLoginRoute = location.pathname === '/login'
  const isKioskTabletRoute = location.pathname === '/kiosk'
  const hasSessionToken = Boolean(db.authStore.token)
  const isSessionValid = db.authStore.isValid
  const searchStr =
    typeof location.search === 'string'
      ? location.search
      : (location as { searchStr?: string }).searchStr ?? window.location.search

  useEffect(() => {
    if (!user && !isLoginRoute && !isKioskTabletRoute) {
      if (!hasSessionToken && !isSessionValid) {
        const redirect = `${location.pathname}${searchStr}${location.hash}`
        router.navigate({ to: '/login', search: { redirect }, replace: true })
      }
      return
    }

  }, [
    user,
    isLoginRoute,
    isKioskTabletRoute,
    router,
    location.pathname,
    searchStr,
    location.hash,
    hasSessionToken,
    isSessionValid,
  ])

  // Validate the stored session once on startup. Keyed on a ref, not the
  // token: refreshSession() itself issues a new token, so a token-dependent
  // effect re-fires on every refresh and loops forever.
  const sessionValidatedRef = useRef(false)
  useEffect(() => {
    if (sessionValidatedRef.current) return
    if (!db.authStore.token || isKioskTabletRoute) return
    sessionValidatedRef.current = true
    refreshSession().catch((err) => {
      console.warn('Failed to refresh session on startup', err)
    })
  }, [db.authStore.token, refreshSession, isKioskTabletRoute])

  useEffect(() => {
    if (!db.authStore.token || !db.authStore.isValid || isKioskTabletRoute) return
    db.hydrateUsers?.().catch((err) => {
      console.warn('Failed to hydrate users on auth/page change', err)
    })
  }, [db, db.authStore.token, db.authStore.isValid, user?.id, location.pathname, isKioskTabletRoute])

  // Unlike user hydration, realtime is needed on the kiosk tablet too — it is
  // how the receptionist panel and the tablet see each other's session edits.
  // Detach on token change/logout so the stream never outlives its session.
  useEffect(() => {
    if (!db.authStore.token || !db.authStore.isValid) return
    db.attachRealtime?.().catch((err) => {
      console.warn('Failed to attach realtime subscriptions', err)
    })
    return () => {
      db.detachRealtime?.()
    }
  }, [db, db.authStore.token, db.authStore.isValid])

  if (!user && !isLoginRoute && !isKioskTabletRoute) {
    return null
  }

  const outlet = (
    <RootErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<RootPending />}>
        <Outlet />
      </Suspense>
    </RootErrorBoundary>
  )

  if (isKioskTabletRoute) {
    return (
      <RouteTheme theme="dark">
        <div className="fixed inset-0 overflow-hidden">{outlet}</div>
      </RouteTheme>
    )
  }

  const appChrome = (
    <FeedbackProvider>
    <FollowUpsProvider>
    <NewCalloutModalProvider>
      <NewSessionModalProvider>
        <NewJobModalProvider>
          <Header
            user={
              user
                ? {
                    name: user.name,
                    avatarUrl: user.avatarUrl,
                    isAdmin: user.isAdmin,
                  }
                : undefined
            }
            onLoginClick={() => {
              router.navigate({ to: '/login' })
            }}
            onProfileClick={() => {
              router.navigate({ to: '/profile' })
            }}
            onLogoutClick={() => {
              logout().then(() => {
                router.navigate({ to: '/login' })
              })
            }}
            kioskMode={false}
          />
          {outlet}
          <AppUpdateNotifier />

	    {/**
	        <TanStackDevtools
	        plugins={[
	        // {
	        // name: 'TanStack Query',
	        // render: <ReactQueryDevtoolsPanel />,
	        // },
	        {name: 'TanStack Router', render: <TanStackRouterDevtoolsPanel />},
	        ]} />
	     **/}
        </NewJobModalProvider>
      </NewSessionModalProvider>
    </NewCalloutModalProvider>
    </FollowUpsProvider>
    </FeedbackProvider>
  )

  if (isLoginRoute) {
    return (
      <RouteTheme theme="dark">
        {appChrome}
      </RouteTheme>
    )
  }

  if (location.pathname === '/') {
    return (
      <RouteTheme theme="dark">
        {appChrome}
      </RouteTheme>
    )
  }

  return (
    <RouteTheme theme="light">
      {appChrome}
    </RouteTheme>
  )
}

const rootRoute = createRootRoute({
  component: RootLayout,
})

const routeTree = registerRoutes(rootRoute as RootRoute)

let router: ReturnType<typeof createRouter>

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

async function bootstrap() {
  await loadRuntimeEnv()
  initSentry()

  if (import.meta.env.DEV) {
    const prefix = getRuntimeEnv().VITE_TITLE_PREFIX?.trim() || ''
    if (prefix) {
      document.title = `${prefix}${document.title}`
    }
  }

  const TanStackQueryProviderContext = TanStackQueryProvider.getContext()

  const pbAuth = createPocketBaseAuth()
  const db = createDb({
    authStore: pbAuth.authStore,
    authApi: pbAuth.authApi,
    pocketBase: pbAuth.pb,
    idGenerator: pocketBaseIdGenerator,
    adapterFactory: ({ seedUsers }) =>
      createPocketBaseTanStackDb({
        pb: pbAuth.pb,
        seedUsers,
        queryClient: TanStackQueryProviderContext.queryClient,
      }),
  })

  if (import.meta.env.DEV) {
    registerDbConsoleRepl(db, { pb: pbAuth.pb })
  }

  router = createRouter({
    routeTree,
    context: {
      ...TanStackQueryProviderContext,
      db,
    },
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultStructuralSharing: true,
    defaultPreloadStaleTime: 0,
  })

  const rootElement = document.getElementById('app')
  if (rootElement && !rootElement.innerHTML) {
    const root = ReactDOM.createRoot(rootElement)
    root.render(
      <StrictMode>
        <DbProvider db={db}>
          <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </TanStackQueryProvider.Provider>
        </DbProvider>
      </StrictMode>,
    )
  }

  // If you want to start measuring performance in your app, pass a function
  // to log results (for example: reportWebVitals(console.log))
  // or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
  reportWebVitals()
}

bootstrap().catch((err) => {
  console.error('Failed to bootstrap app', err)
  Sentry.captureException(err)
})
